import { recordBootEvent } from '../../bootstrap/boot-trace';
import {
  WORKSPACE_METADATA_SCHEMA_VERSION,
  type WorkspaceListMetadata,
  type WorkspaceMetadataPendingMutation,
  type WorkspaceMetadataShapeState,
} from './types';

function metadataRevisionEpoch(node: WorkspaceListMetadata): number | null {
  const revisions =
    node.kind === 'folder'
      ? [node.subtreeRevision]
      : [node.subtreeRevision, node.updatedAt];
  for (const revision of revisions) {
    if (!revision) continue;
    const epoch = Date.parse(revision);
    if (Number.isFinite(epoch)) return epoch;
  }
  return null;
}

function isOlderServerRevision(
  candidate: WorkspaceListMetadata,
  current: WorkspaceListMetadata
): boolean {
  const candidateRevision = metadataRevisionEpoch(candidate);
  const currentRevision = metadataRevisionEpoch(current);
  return (
    candidateRevision !== null &&
    currentRevision !== null &&
    candidateRevision < currentRevision
  );
}

export interface WorkspaceMetadataStorageDriver {
  read<T>(key: string): Promise<T | null>;
  write<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
}

export interface WorkspaceMetadataStoreScope extends Pick<
  WorkspaceMetadataShapeState,
  | 'accountId'
  | 'principalId'
  | 'serverId'
  | 'serverBaseUrl'
  | 'visibilityMode'
  | 'workspaceId'
  | 'tenantId'
  | 'shapeKey'
> {
  permissionGraphVersion: number | null;
}

export class WorkspaceMetadataStore {
  private readonly canonicalNodes = new Map<string, WorkspaceListMetadata>();
  private readonly pending = new Map<
    string,
    WorkspaceMetadataPendingMutation
  >();
  private shapeState: WorkspaceMetadataShapeState;
  private hydratePromise: Promise<void> | null = null;
  private hydrated = false;
  /** The state last read from or written to storage, as JSON: persist
   * writes only a changed state. */
  private storedState: string | null = null;

  constructor(
    private readonly scope: WorkspaceMetadataStoreScope,
    private readonly storage: WorkspaceMetadataStorageDriver = new IndexedDbMetadataDriver()
  ) {
    this.shapeState = this.baseShapeState();
  }

  async hydrate(): Promise<void> {
    if (this.hydrated) return;
    if (this.hydratePromise) return this.hydratePromise;
    this.hydratePromise = this.hydrateFromStorage().finally(() => {
      this.hydrated = true;
      this.hydratePromise = null;
    });
    return this.hydratePromise;
  }

  private async hydrateFromStorage(): Promise<void> {
    const startedAt = performance.now();
    let outcome = 'error';
    let nodeCount = 0;
    let pendingCount = 0;
    recordBootEvent('metadata-cache-hydration-started', {
      shape: this.scope.shapeKey,
    });
    try {
      const readStartedAt = performance.now();
      const persisted =
        await this.storage.read<WorkspaceMetadataPersistedState>(
          this.storageKey()
        );
      this.storedState = persisted ? JSON.stringify(persisted) : null;
      recordBootEvent('metadata-cache-storage-read', {
        durationMs: performance.now() - readStartedAt,
        found: Boolean(persisted),
        shape: this.scope.shapeKey,
      });
      if (
        !persisted ||
        persisted.version !== 2 ||
        !this.isCompatibleEnvelopeScope(persisted.shapeState)
      ) {
        outcome = persisted ? 'incompatible' : 'missing';
        await this.storage.delete(this.storageKey());
        this.storedState = null;
        return;
      }
      const applyStartedAt = performance.now();
      this.canonicalNodes.clear();
      this.pending.clear();
      this.shapeState = this.isCompatibleShapeGeneration(persisted.shapeState)
        ? persisted.shapeState
        : this.baseShapeState();
      const pending = persisted.pending.filter(mutation =>
        this.isSameActorScope(mutation)
      );
      pendingCount = pending.length;
      for (const mutation of pending) {
        this.pending.set(mutation.localMutationId, mutation);
      }
      const pendingNodeIds = new Set(pending.map(mutation => mutation.nodeId));
      const compatibleGeneration = this.isCompatibleShapeGeneration(
        persisted.shapeState
      );
      const seedNodes = compatibleGeneration
        ? persisted.nodes
        : persisted.pendingNodes;
      for (const node of seedNodes) {
        if (compatibleGeneration || pendingNodeIds.has(node.nodeId)) {
          this.canonicalNodes.set(node.nodeId, node);
        }
      }
      nodeCount = this.canonicalNodes.size;
      outcome = compatibleGeneration ? 'restored' : 'pending-only';
      recordBootEvent('metadata-cache-state-applied', {
        durationMs: performance.now() - applyStartedAt,
        nodeCount,
        pendingCount,
        shape: this.scope.shapeKey,
      });
    } finally {
      recordBootEvent('metadata-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        nodeCount,
        outcome,
        pendingCount,
        shape: this.scope.shapeKey,
      });
    }
  }

  async applyServerNodes(
    nodes: WorkspaceListMetadata[],
    shapeStatePatch: Pick<WorkspaceMetadataShapeState, 'token' | 'cursor'> &
      Partial<Pick<WorkspaceMetadataShapeState, 'pageCursors'>>
  ): Promise<void> {
    return this.reconcileServerNodes(nodes, [], shapeStatePatch);
  }

  /**
   * Applies the server-authoritative permission projection without replacing
   * newer folder metadata.
   *
   * Folder roles can change without changing the folder subtree revision. A
   * child-window response may therefore carry the current metadataOnly state
   * together with an older title/hierarchy revision. The normal server-node
   * merge must keep rejecting that older metadata, while this narrow merge
   * still updates the permission fields.
   */
  async applyServerPermissionProjection(
    nodes: readonly WorkspaceListMetadata[]
  ): Promise<void> {
    await this.hydrate();
    const previousNodes = new Map(this.canonicalNodes);
    try {
      for (const node of nodes) {
        if (node.kind !== 'folder' || !node.folder) continue;
        const current = this.canonicalNodes.get(node.nodeId);
        if (current?.kind !== 'folder' || !current.folder) continue;
        this.canonicalNodes.set(node.nodeId, {
          ...current,
          permissionHint: node.permissionHint,
          folder: {
            ...current.folder,
            metadataOnly: node.folder.metadataOnly,
          },
        });
      }
      await this.persist();
    } catch (error) {
      this.canonicalNodes.clear();
      for (const [nodeId, node] of previousNodes) {
        this.canonicalNodes.set(nodeId, node);
      }
      throw error;
    }
  }

  async reconcileServerNodes(
    nodes: WorkspaceListMetadata[],
    removeNodeIds: readonly string[],
    shapeStatePatch: Pick<WorkspaceMetadataShapeState, 'token' | 'cursor'> &
      Partial<Pick<WorkspaceMetadataShapeState, 'pageCursors'>>
  ): Promise<void> {
    await this.hydrate();
    const previousNodes = new Map(this.canonicalNodes);
    const previousPending = new Map(this.pending);
    const previousShapeState = this.shapeState;
    try {
      for (const nodeId of removeNodeIds) {
        this.canonicalNodes.delete(nodeId);
        for (const [mutationId, mutation] of this.pending) {
          if (mutation.nodeId === nodeId) {
            this.pending.delete(mutationId);
          }
        }
      }
      for (const node of nodes) {
        const canonical = { ...node, syncState: 'fresh' as const };
        const current = this.canonicalNodes.get(canonical.nodeId);
        if (
          current?.syncState === 'fresh' &&
          isOlderServerRevision(canonical, current)
        ) {
          continue;
        }
        this.canonicalNodes.set(canonical.nodeId, canonical);
        this.ackMatchingPending(canonical);
      }
      await this.persist(shapeStatePatch);
    } catch (error) {
      this.canonicalNodes.clear();
      this.pending.clear();
      for (const [nodeId, node] of previousNodes) {
        this.canonicalNodes.set(nodeId, node);
      }
      for (const [mutationId, mutation] of previousPending) {
        this.pending.set(mutationId, mutation);
      }
      this.shapeState = previousShapeState;
      throw error;
    }
  }

  async applyLocalNodes(nodes: WorkspaceListMetadata[]): Promise<void> {
    await this.hydrate();
    for (const node of nodes) {
      this.canonicalNodes.set(node.nodeId, {
        ...node,
        syncState: 'local',
      });
    }
    await this.persist();
  }

  /**
   * Persists local document placement without replacing server-authoritative
   * folder metadata. Callers that only changed folder membership must not
   * revive an older title, visibility, hierarchy, or subtree projection.
   */
  async applyLocalNodeDocIds(
    nodes: readonly { nodeId: string; docIds: readonly string[] }[]
  ): Promise<void> {
    await this.hydrate();
    const previousNodes = new Map<string, WorkspaceListMetadata>();
    for (const { nodeId, docIds } of nodes) {
      const current = this.canonicalNodes.get(nodeId);
      if (!current) continue;
      previousNodes.set(nodeId, current);
      this.canonicalNodes.set(nodeId, {
        ...current,
        docIds: [...docIds],
      });
    }
    try {
      await this.persist();
    } catch (error) {
      for (const [nodeId, previousNode] of previousNodes) {
        this.canonicalNodes.set(nodeId, previousNode);
      }
      throw error;
    }
  }

  async applyLocalMutation(
    nodes: WorkspaceListMetadata[],
    mutation: WorkspaceMetadataPendingMutation
  ): Promise<void> {
    return this.applyLocalMutations(nodes, [mutation]);
  }

  async applyLocalMutations(
    nodes: WorkspaceListMetadata[],
    mutations: WorkspaceMetadataPendingMutation[]
  ): Promise<void> {
    await this.hydrate();
    if (mutations.some(mutation => !this.isSameActorScope(mutation))) {
      throw new Error('Workspace metadata pending mutation scope mismatch');
    }

    const previousNodes = new Map<string, WorkspaceListMetadata | undefined>();
    for (const node of nodes) {
      previousNodes.set(node.nodeId, this.canonicalNodes.get(node.nodeId));
    }
    const previousMutations = new Map(
      mutations.map(mutation => [
        mutation.localMutationId,
        this.pending.get(mutation.localMutationId),
      ])
    );

    try {
      for (const node of nodes) {
        if (!this.canonicalNodes.has(node.nodeId)) {
          this.canonicalNodes.set(node.nodeId, {
            ...node,
            syncState: 'local',
          });
        }
      }
      for (const mutation of mutations) {
        this.pending.set(mutation.localMutationId, mutation);
      }
      await this.persist();
    } catch (error) {
      for (const [nodeId, previousNode] of previousNodes) {
        if (previousNode) {
          this.canonicalNodes.set(nodeId, previousNode);
        } else {
          this.canonicalNodes.delete(nodeId);
        }
      }
      for (const mutation of mutations) {
        const previousMutation = previousMutations.get(
          mutation.localMutationId
        );
        if (previousMutation) {
          this.pending.set(mutation.localMutationId, previousMutation);
        } else {
          this.pending.delete(mutation.localMutationId);
        }
      }
      throw error;
    }
  }

  async removeNodes(
    nodeIds: readonly string[],
    shapeStatePatch: Partial<
      Pick<WorkspaceMetadataShapeState, 'token' | 'cursor' | 'pageCursors'>
    > = {}
  ): Promise<void> {
    await this.hydrate();
    for (const nodeId of nodeIds) {
      this.canonicalNodes.delete(nodeId);
      for (const [mutationId, mutation] of this.pending) {
        if (mutation.nodeId === nodeId) {
          this.pending.delete(mutationId);
        }
      }
    }
    await this.persist(shapeStatePatch);
  }

  async clear(): Promise<void> {
    await this.hydrate();
    this.canonicalNodes.clear();
    this.pending.clear();
    this.shapeState = this.baseShapeState();
    await this.persist();
  }

  /**
   * Drops the server-authoritative window while retaining local mutations and
   * the nodes they overlay. Permission graph changes use this before loading a
   * fresh bounded shape so revoked server rows cannot survive a reset, while
   * offline creates and moves remain available for outbox reconciliation.
   */
  async resetServerProjection(): Promise<void> {
    await this.hydrate();
    const pendingNodeIds = new Set(
      [...this.pending.values()].map(mutation => mutation.nodeId)
    );
    for (const nodeId of this.canonicalNodes.keys()) {
      if (!pendingNodeIds.has(nodeId)) {
        this.canonicalNodes.delete(nodeId);
      }
    }
    this.shapeState = this.baseShapeState();
    await this.persist();
  }

  async enqueuePending(
    mutation: WorkspaceMetadataPendingMutation
  ): Promise<void> {
    await this.hydrate();
    if (!this.isSameActorScope(mutation)) {
      throw new Error('Workspace metadata pending mutation scope mismatch');
    }
    this.pending.set(mutation.localMutationId, mutation);
    await this.persist();
  }

  async ackPending(localMutationId: string): Promise<void> {
    await this.hydrate();
    const mutation = this.pending.get(localMutationId);
    if (!mutation) return;
    this.pending.delete(localMutationId);
    if (mutation.op === 'delete') {
      this.canonicalNodes.delete(mutation.nodeId);
      await this.persist();
      return;
    }
    const current = this.canonicalNodes.get(mutation.nodeId);
    if (current) {
      this.canonicalNodes.set(mutation.nodeId, {
        ...current,
        ...mutation.patch,
        syncState: 'fresh',
      });
    }
    await this.persist();
  }

  async rejectPending(localMutationId: string): Promise<void> {
    await this.hydrate();
    const mutation = this.pending.get(localMutationId);
    if (!mutation) return;
    this.pending.delete(localMutationId);
    const current = this.canonicalNodes.get(mutation.nodeId);
    if (mutation.op === 'create' && current?.syncState === 'local') {
      this.canonicalNodes.delete(mutation.nodeId);
    }
    await this.persist();
  }

  async rejectPendingForNode(
    nodeId: string,
    operations?: readonly WorkspaceMetadataPendingMutation['op'][]
  ): Promise<void> {
    return this.rejectPendingForNodes([nodeId], operations);
  }

  async rejectPendingForNodes(
    nodeIds: readonly string[],
    operations?: readonly WorkspaceMetadataPendingMutation['op'][]
  ): Promise<void> {
    await this.hydrate();
    const acceptedNodeIds = new Set(nodeIds);
    const acceptedOperations = operations ? new Set(operations) : null;
    for (const [mutationId, mutation] of this.pending) {
      if (
        acceptedNodeIds.has(mutation.nodeId) &&
        (!acceptedOperations || acceptedOperations.has(mutation.op))
      ) {
        this.pending.delete(mutationId);
      }
    }
    for (const nodeId of acceptedNodeIds) {
      const current = this.canonicalNodes.get(nodeId);
      if (
        current?.syncState === 'local' &&
        ![...this.pending.values()].some(mutation => mutation.nodeId === nodeId)
      ) {
        this.canonicalNodes.delete(nodeId);
      }
    }
    await this.persist();
  }

  getVisibleNodes(): WorkspaceListMetadata[] {
    return this.applyPendingOverlays([...this.canonicalNodes.values()]).sort(
      (left, right) => left.sortKey.localeCompare(right.sortKey)
    );
  }

  applyPendingOverlays(
    nodes: readonly WorkspaceListMetadata[]
  ): WorkspaceListMetadata[] {
    return nodes
      .map(node => this.applyPendingOverlay(node))
      .filter((node): node is WorkspaceListMetadata => node !== null);
  }

  getCanonicalNodes(): WorkspaceListMetadata[] {
    return [...this.canonicalNodes.values()].map(node => ({ ...node }));
  }

  getPendingMutations(): WorkspaceMetadataPendingMutation[] {
    return [...this.pending.values()].map(mutation => ({
      ...mutation,
      patch: { ...mutation.patch },
    }));
  }

  getShapeState(): WorkspaceMetadataShapeState {
    return { ...this.shapeState };
  }

  async updateShapeState(
    patch: Partial<
      Pick<
        WorkspaceMetadataShapeState,
        'token' | 'cursor' | 'pageCursors' | 'permissionGraphVersion'
      >
    >
  ): Promise<void> {
    await this.hydrate();
    await this.persist(patch);
  }

  async updatePageCursor(key: string, cursor: string | null): Promise<void> {
    await this.hydrate();
    await this.persist({
      pageCursors: {
        ...this.shapeState.pageCursors,
        [key]: cursor,
      },
    });
  }

  private applyPendingOverlay(
    node: WorkspaceListMetadata
  ): WorkspaceListMetadata | null {
    const nodeMutations = [...this.pending.values()]
      .filter(candidate => candidate.nodeId === node.nodeId)
      .sort(
        (left, right) =>
          left.createdAt.localeCompare(right.createdAt) ||
          left.localMutationId.localeCompare(right.localMutationId)
      );
    if (nodeMutations.some(mutation => mutation.op === 'delete')) {
      return null;
    }
    if (nodeMutations.length === 0) {
      return node;
    }
    return {
      ...nodeMutations.reduce<WorkspaceListMetadata>(
        (visible, mutation) => ({ ...visible, ...mutation.patch }),
        node
      ),
      syncState: 'pending',
    };
  }

  private ackMatchingPending(node: WorkspaceListMetadata): void {
    for (const [mutationId, mutation] of this.pending) {
      if (
        mutation.op !== 'delete' &&
        mutation.nodeId === node.nodeId &&
        (mutation.op === 'create' ||
          this.pendingPatchMatchesNode(mutation.patch, node))
      ) {
        this.pending.delete(mutationId);
      }
    }
  }

  private pendingPatchMatchesNode(
    patch: Partial<WorkspaceListMetadata>,
    node: WorkspaceListMetadata
  ): boolean {
    return Object.entries(patch).every(([key, value]) => {
      const nodeValue = node[key as keyof WorkspaceListMetadata];
      if (
        value !== null &&
        typeof value === 'object' &&
        nodeValue !== null &&
        typeof nodeValue === 'object'
      ) {
        return JSON.stringify(nodeValue) === JSON.stringify(value);
      }
      return nodeValue === value;
    });
  }

  private async persist(
    patch: Partial<WorkspaceMetadataShapeState> = {}
  ): Promise<void> {
    if (
      patch.permissionGraphVersion !== undefined &&
      patch.permissionGraphVersion !== this.scope.permissionGraphVersion
    ) {
      throw new Error('Workspace metadata permission scope mismatch');
    }
    this.shapeState = {
      ...this.shapeState,
      ...patch,
      permissionGraphVersion: this.scope.permissionGraphVersion,
    };
    const pendingNodeIds = new Set(
      [...this.pending.values()].map(mutation => mutation.nodeId)
    );
    const state: WorkspaceMetadataPersistedState = {
      shapeState: this.shapeState,
      nodes: [...this.canonicalNodes.values()],
      pending: [...this.pending.values()],
      pendingNodes: [...this.canonicalNodes.values()].filter(node =>
        pendingNodeIds.has(node.nodeId)
      ),
      version: 2,
    };
    const serialized = JSON.stringify(state);
    if (serialized === this.storedState) return;
    await this.storage.write(this.storageKey(), state);
    this.storedState = serialized;
  }

  private isCompatibleEnvelopeScope(
    state: WorkspaceMetadataShapeState
  ): boolean {
    const expected = this.baseShapeState();
    return (
      state.accountId === expected.accountId &&
      state.principalId === expected.principalId &&
      state.serverId === expected.serverId &&
      state.serverBaseUrl === expected.serverBaseUrl &&
      state.visibilityMode === expected.visibilityMode &&
      state.workspaceId === expected.workspaceId &&
      state.tenantId === expected.tenantId &&
      state.shapeKey === expected.shapeKey &&
      state.schemaVersion === WORKSPACE_METADATA_SCHEMA_VERSION
    );
  }

  private isCompatibleShapeGeneration(
    state: WorkspaceMetadataShapeState
  ): boolean {
    return (
      this.isCompatibleEnvelopeScope(state) &&
      state.permissionGraphVersion === this.scope.permissionGraphVersion
    );
  }

  private isSameActorScope(
    mutation: WorkspaceMetadataPendingMutation
  ): boolean {
    return (
      mutation.accountId === this.scope.accountId &&
      mutation.principalId === this.scope.principalId &&
      mutation.serverId === this.scope.serverId &&
      mutation.serverBaseUrl === this.scope.serverBaseUrl &&
      mutation.visibilityMode === this.scope.visibilityMode &&
      mutation.workspaceId === this.scope.workspaceId &&
      mutation.tenantId === this.scope.tenantId &&
      mutation.shapeKey === this.scope.shapeKey &&
      mutation.schemaVersion === WORKSPACE_METADATA_SCHEMA_VERSION &&
      (mutation.permissionGraphVersion === null ||
        this.scope.permissionGraphVersion === null ||
        mutation.permissionGraphVersion <= this.scope.permissionGraphVersion)
    );
  }

  private baseShapeState(): WorkspaceMetadataShapeState {
    return {
      ...this.scope,
      permissionGraphVersion: this.scope.permissionGraphVersion,
      schemaVersion: WORKSPACE_METADATA_SCHEMA_VERSION,
      token: null,
      cursor: null,
      pageCursors: {},
    };
  }

  private storageKey(): string {
    return [
      'workspace-metadata',
      'v2',
      this.scope.serverId,
      this.scope.serverBaseUrl,
      this.scope.tenantId,
      this.scope.workspaceId,
      this.scope.accountId,
      this.scope.principalId,
      this.scope.visibilityMode,
      this.scope.shapeKey,
      WORKSPACE_METADATA_SCHEMA_VERSION,
    ].join(':');
  }
}

interface WorkspaceMetadataPersistedState {
  shapeState: WorkspaceMetadataShapeState;
  nodes: WorkspaceListMetadata[];
  pending: WorkspaceMetadataPendingMutation[];
  pendingNodes: WorkspaceListMetadata[];
  version: 2;
}

class IndexedDbMetadataDriver implements WorkspaceMetadataStorageDriver {
  private readonly dbName = 'streamlain-workspace-metadata';
  private readonly storeName = 'shape-state';
  private dbPromise: Promise<IDBDatabase | null> | null = null;

  async read<T>(key: string): Promise<T | null> {
    const db = await this.open();
    if (!db) return null;
    return new Promise<T | null>((resolve, reject) => {
      const request = db
        .transaction(this.storeName, 'readonly')
        .objectStore(this.storeName)
        .get(key);
      request.onsuccess = () =>
        resolve((request.result as T | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  async write<T>(key: string, value: T): Promise<void> {
    const db = await this.open();
    if (!db) return;
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      transaction.objectStore(this.storeName).put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this.open();
    if (!db) return;
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      transaction.objectStore(this.storeName).delete(key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  private open(): Promise<IDBDatabase | null> {
    if (this.dbPromise) {
      return this.dbPromise;
    }
    this.dbPromise = new Promise(resolve => {
      if (typeof globalThis.indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const request = globalThis.indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
    return this.dbPromise;
  }
}
